import { fmtCompact } from "../kit";
import { toast } from "../toast";
import { marksIcon, priceHtml } from "./econArt";
import { icon } from "../iconsKit";
import { priceButtonHtml } from "../components";
import { confirmPurchase, econCue, purchasePop, tickPill } from "./purchaseFx";
import { refusalText } from "./shopActions";
import { SKIN_TABS, swatchCss } from "./skinsModel";
import { skinsLink } from "./skinsLink";
import { petLink } from "../pets/petLink";
import { openSkins } from "./skinsSheet";
import { openSkinPreview, shopSkinCard, type ShopSkin } from "./skinPreviewCard";
import type { SkinCard } from "./skinsModel";
import type { SkinKind } from "@highmarch/domain";
import { SKIN_LOOKS } from "../../render/skins/looks";
import { notEnoughGems } from "./getGold";

/**
 * Where skins are sold besides the picker (FINISH_PLAN WP8): a Skins shelf on the Shop's Gems and Marks pages
 * (shopSheet.ts) with the skins that currency buys — each opens the picker on its preview — and the Black Market's exclusive skin on the days it
 * features one (marketSheet.ts), bought as shown for gems like its gear piece. Both are the player's own pages: no
 * offer is pushed anywhere (ENGAGEMENT C).
 */

/** What a shelf card's skin dresses, under its name ("Ivory" alone did not say tower, fortress or troops). */
function skinFor(kind: string): string {
  if (kind === "tower") return "Any tower";
  if (kind === "pet") return "Pet coat";
  return SKIN_TABS.find((t) => t.kind === kind)?.label ?? "";
}

/** Shop cards shown before "ALL SKINS": two rows of three. */
const SHELF_MAX = 6;

/**
 * The Shop's Skins section for one currency's page (MP-10, 2026-10-12: the Gems page used to list the Marks skins too,
 * their prices next to gem prices in the same style): the skins `currency` buys, each price chip with its currency's
 * painted icon, and on the Gems page a "More for Marks" link to the Marks page's shelf when Marks skins are for sale.
 * Appended to `host`; null-safe before boot.
 */
export function mountSkinShelf(host: HTMLElement, parent: HTMLElement, currency: "gems" | "marks" = "gems", toMarks: (() => void) | null = null): void {
  const link = skinsLink();
  if (!link) return;
  const k = link.cosmetics;
  // a pet's skins go on the shelf once that pet is had (docs/PETS.md)
  const unowned = k.catalogue.filter((s) => (s.unlock.by === "gems" || s.unlock.by === "marks") && !k.state.owned.has(s.id) && (s.kind !== "pet" || petLink.has(s.pet ?? "")));
  const forSale = unowned.filter((s) => s.unlock.by === currency).slice(0, SHELF_MAX);
  const marksMore = currency === "gems" && toMarks !== null ? unowned.filter((s) => s.unlock.by === "marks").length : 0;
  if (forSale.length === 0 && currency === "marks") return;
  const cards = forSale.map((s) => {
    const u = s.unlock as { by: "gems" | "marks"; price: number }, look = SKIN_LOOKS[s.id];
    return `<button class="sks" data-id="${s.id}" data-k="${s.kind}"><span class="skd" style="background:${look ? swatchCss(look) : ""}"></span><b>${look?.name ?? s.id}</b><small class="skk">${skinFor(s.kind)}</small>${priceHtml(u.by, fmtCompact(u.price), 16)}</button>`;
  }).join("");
  const more = marksMore > 0 ? `<button type="button" class="skmore">${marksIcon(18)}<span>${marksMore} more skin${marksMore === 1 ? "" : "s"} for Marks</span>${icon("chev", 14)}</button>` : "";
  // no tagline: the heading names what pays for the shelf
  const head = currency === "gems" ? "Gem skins" : "Marks skins";
  host.insertAdjacentHTML("beforeend", `<section class="ssec skshelf c-${currency}"><div class="shd"><b class="disp">${head}</b></div>
    ${cards ? `<div class="sgrid">${cards}</div>` : ""}${more}<button class="btn blue all"><span class="disp">ALL SKINS</span></button></section>`);
  const shelf = host.querySelector<HTMLElement>(`.skshelf.c-${currency}`)!;
  for (const b of shelf.querySelectorAll<HTMLButtonElement>(".sks")) b.onclick = () => { openSkins(parent, { kind: b.dataset.k as never, select: b.dataset.id }); };
  shelf.querySelector<HTMLButtonElement>(".all")!.onclick = () => { openSkins(parent, { kind: "tower" }); };
  const m = shelf.querySelector<HTMLButtonElement>(".skmore");
  if (m && toMarks) m.onclick = toMarks;
}

/** The Black Market page's exclusive skin card on a day it features one (after the gear piece). */
export function mountBlackMarketSkin(body: HTMLElement, parent: HTMLElement, rerender: () => void): void {
  const link = skinsLink();
  if (!link) return;
  const k = link.cosmetics, now = link.now(), s = k.blackMarketSkin(now), look = s ? SKIN_LOOKS[s.id] : undefined;
  if (!s || !look || s.unlock.by !== "blackMarket") return;
  const price = s.unlock.price, ok = k.wallet.canAfford("gems", price);
  // a navy plate like the day's gear piece above it: the swatch in a well, the gold gem price (red amount when short)
  const html = `<div class="k-card plate bmskin"><span class="k-well sm"><span class="skd" style="background:${swatchCss(look)}"></span></span><div class="t"><b class="disp">${look.name}</b><small>Exclusive ${s.kind === "tower" ? "tower" : s.kind} skin · only here, some days</small></div>
    ${priceButtonHtml({ currency: "gems", amount: fmtCompact(price), short: !ok, className: "bmsk" })}</div>`;
  const foot = body.querySelector(".foot");
  if (foot) foot.insertAdjacentHTML("beforebegin", html); else body.insertAdjacentHTML("beforeend", html);
  const card = body.querySelector<HTMLElement>(".bmskin")!;
  card.querySelector<HTMLButtonElement>(".bmsk")!.onclick = async () => {
    if (!k.wallet.canAfford("gems", price)) { void notEnoughGems(parent, price - k.wallet.gems, card.closest(".sheet")); return; }
    const yes = await confirmPurchase(parent, { title: "BLACK MARKET", currency: "gems", price, className: "bm",
      body: `<div class="sk-dlg"><span class="skd" style="background:${swatchCss(look)}"></span><div><b class="disp">${look.name}</b><small>${look.blurb}</small></div></div><p>Yours for good${s.kind === "tower" ? ", on any tower" : ""}. Looks only, pure glory.</p>` });
    if (!yes) return;
    const before = k.wallet.gems, r = k.buyBlackMarketSkin(s.id, link.now(), "hub");
    if (r !== true) { toast(parent, refusalText(r)); rerender(); return; }
    econCue("buy");
    purchasePop(card);
    link.changed();
    tickPill("gems", before, k.wallet.gems);
    toast(parent, `${look.name} is yours: wear it from Towers → Skins`);
    rerender();
  };
}

/** The Shop's Skins tab sections, in order (a pet's coats once the pet is had). */
const SHOP_KINDS: readonly { readonly kind: SkinKind; readonly title: string }[] = [
  { kind: "tower", title: "Towers" }, { kind: "fortress", title: "Fortress" }, { kind: "troop", title: "Troops" }, { kind: "pet", title: "Pets" },
  { kind: "emblem", title: "Banners" }, { kind: "road", title: "Roads" }, { kind: "frame", title: "Title frames" }, { kind: "kill", title: "Kill effects" },
];

/** Every skin of `kind` the Shop lists, each with who it goes on (tower skins on the first tower that may wear them). */
function shopSkinsOf(kind: SkinKind): ShopSkin[] {
  const link = skinsLink();
  if (!link) return [];
  const k = link.cosmetics;
  const seen = new Set<string>(), out: ShopSkin[] = [];
  const add = (who: string | null) => { for (const s of k.list(kind, who)) if (!seen.has(s.id) && SKIN_LOOKS[s.id]) { seen.add(s.id); out.push({ id: s.id, kind, who }); } };
  if (kind === "tower") for (const t of link.towers()) add(t.id);
  else if (kind === "pet") for (const p of petLink.owned()) add(p.id);
  else add(null);
  return out;
}

/**
 * The Shop's Skins tab (owner 2026-10-20: no "All skins" button, the Shop has the skins): every skin, by what it
 * dresses, as item tiles (the picture, the name, the price strip or what it takes); for sale first, then yours, then
 * the ones earned elsewhere. A tap opens its preview (skinPreviewCard.ts).
 */
export function mountSkinsShop(host: HTMLElement, parent: HTMLElement, rerender: () => void): void {
  const link = skinsLink();
  const sections = SHOP_KINDS.map(({ kind, title }) => {
    const rank = (c: SkinCard) => (c.access.have ? 1 : c.access.by === "gems" || c.access.by === "marks" || c.gems ? 0 : 2);
    const cards = shopSkinsOf(kind).map((s) => ({ s, c: shopSkinCard(s) })).filter((x): x is { s: ShopSkin; c: SkinCard } => x.c !== null)
      .sort((a, b) => rank(a.c) - rank(b.c));
    if (cards.length === 0) return "";
    const tiles = cards.map(({ s, c }) => {
      const a = c.access;
      const foot = a.have ? `<span class="skf${c.worn ? " on" : ""}">${c.worn ? "Worn" : "Yours"}</span>`
        : a.by === "gems" || a.by === "marks" ? `<span class="btn k-price${a.affordable ? "" : " short"}"><span class="k-cur">${priceHtml(a.by, "", 21)}</span><span class="k-amt">${fmtCompact(a.price)}</span></span>`
        : c.gems ? `<span class="btn k-price${c.gems.affordable ? "" : " short"}"><span class="k-cur">${priceHtml("gems", "", 21)}</span><span class="k-amt">${fmtCompact(c.gems.price)}</span></span>`
        : `<span class="skf lk">${icon("lock", 11)}${c.status}</span>`;
      // owner 2026-10-21: each skin framed in its tier's colour (its price band; the Wyrm's Mythic)
      const sd = link?.cosmetics.skin(s.id), u = sd?.unlock;
      const tier = !sd || !u ? "common" : u.by === "wyrm" ? "mythic" : "band" in u ? u.band : sd.buyGemsBand ?? "rare";
      return `<button type="button" class="k-tile sk2 sr-${tier}${a.have ? " have" : ""}" data-id="${s.id}" data-k="${kind}" data-w="${s.who ?? ""}">
        <span class="k-well skw" style="background:${swatchCss(c.look)}"></span><span class="k-name">${c.look.name}${kind === "tower" && link?.cosmetics.skin(s.id)?.tower ? `<small>${link.towers().find((t) => t.id === s.who)?.name ?? ""}</small>` : ""}</span>${foot}</button>`;
    }).join("");
    return `<section class="ssec skins2 k-${kind}"><div class="shd"><b class="disp">${title}</b></div><div class="k-grid3">${tiles}</div></section>`;
  }).join("");
  host.insertAdjacentHTML("beforeend", sections);
  for (const b of host.querySelectorAll<HTMLButtonElement>(".sk2")) {
    b.onclick = () => { openSkinPreview(parent, { id: b.dataset.id!, kind: b.dataset.k as SkinKind, who: b.dataset.w || null }, rerender); };
  }
}
